import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const FONT = 'Inter, sans-serif';
const BLUE = '#155BCB';
const CHECK_INTERVAL_MS = 60 * 1000; // Verifica a cada 1 minuto
const WARNING_BEFORE_MS = 5 * 60 * 1000; // Avisa 5 minutos antes de expirar

/**
 * Decodifica o payload de um JWT sem dependência externa.
 */
const decodeToken = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Limpa todos os dados de sessão do localStorage.
 */
const clearSession = () => {
  const keys = [
    'token', 'id_usuario', 'permissoes', 'cliente_id',
    'orgao_id', 'selected_orgao_id', 'isAdmin', 'selected_cliente_id',
  ];
  keys.forEach((key) => localStorage.removeItem(key));
};

/**
 * SessionValidator — Componente que monitora a validade da sessão JWT.
 * 
 * - Verifica periodicamente se o token expirou
 * - Intercepta respostas 401 (token rejeitado pelo back-end)
 * - Mostra modal de aviso e redireciona para login
 */
const SessionValidator = ({ children }) => {
  const navigate = useNavigate();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const intervalRef = useRef(null);
  const warningShownRef = useRef(false);

  const handleSessionExpired = useCallback(() => {
    setShowWarningModal(false);
    setShowExpiredModal(true);
  }, []);

  const handleLogout = useCallback(() => {
    clearSession();
    setShowExpiredModal(false);
    setShowWarningModal(false);
    navigate('/');
  }, [navigate]);

  // Verifica o token periodicamente
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      const payload = decodeToken(token);
      if (!payload || !payload.exp) {
        handleSessionExpired();
        return;
      }

      const now = Date.now();
      const expiresAt = payload.exp * 1000; // JWT exp é em segundos
      const timeLeft = expiresAt - now;

      // Token já expirou
      if (timeLeft <= 0) {
        handleSessionExpired();
        return;
      }

      // Aviso de expiração próxima (5 min antes)
      if (timeLeft <= WARNING_BEFORE_MS && !warningShownRef.current) {
        warningShownRef.current = true;
        setShowWarningModal(true);
      }
    };

    // Verifica imediatamente ao montar
    checkToken();

    // Verifica periodicamente
    intervalRef.current = setInterval(checkToken, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [handleSessionExpired]);

  // Intercepta respostas 401 da API (fetch global)
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response.status === 401) {
        const token = localStorage.getItem('token');
        if (token) {
          // Token existe mas foi rejeitado → sessão expirou
          handleSessionExpired();
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [handleSessionExpired]);

  // Reset do warning quando o token é renovado
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'token' && e.newValue) {
        warningShownRef.current = false;
        setShowWarningModal(false);
        setShowExpiredModal(false);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <>
      {children}

      {/* Modal de Aviso (5 min antes) */}
      {showWarningModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <span style={{ fontSize: '24px' }}>⏱️</span>
              </div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1F2937', fontFamily: FONT }}>
                Sessão expirando
              </h2>
            </div>
            <p style={{ fontSize: '14px', color: '#6B7280', textAlign: 'center', margin: '0 0 24px', fontFamily: FONT, lineHeight: 1.5 }}>
              Sua sessão irá expirar em breve. Salve seu trabalho para não perder alterações.
            </p>
            <button
              onClick={() => setShowWarningModal(false)}
              style={{ ...btnStyle, backgroundColor: BLUE, color: '#fff', width: '100%' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1249a8')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal de Sessão Expirada */}
      {showExpiredModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <span style={{ fontSize: '24px' }}>🔒</span>
              </div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1F2937', fontFamily: FONT }}>
                Sessão expirada
              </h2>
            </div>
            <p style={{ fontSize: '14px', color: '#6B7280', textAlign: 'center', margin: '0 0 24px', fontFamily: FONT, lineHeight: 1.5 }}>
              Sua sessão expirou por inatividade. Por favor, faça login novamente para continuar.
            </p>
            <button
              onClick={handleLogout}
              style={{ ...btnStyle, backgroundColor: '#DC2626', color: '#fff', width: '100%' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#B91C1C')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#DC2626')}
            >
              Fazer Login
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
};

const modalStyle = {
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '32px',
  width: '100%',
  maxWidth: '380px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  fontFamily: 'Inter, sans-serif',
};

const btnStyle = {
  padding: '12px 24px',
  border: 'none',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  transition: 'background-color 0.15s ease',
};

export default SessionValidator;
